import { redirect } from "@tanstack/react-router"
import type { RegistrationFormValues } from "../form-validation"
import { getOnboardingFormValues } from "./onboarding-store"

export type SignupStepId =
	| "language"
	| "name"
	| "gender"
	| "city"
	| "heard-about"
	| "intention"
	| "instagram"
	| "email"

export type SignupStepPath =
	| "/onboarding/language"
	| "/onboarding/name"
	| "/onboarding/signup-gender"
	| "/onboarding/signup-city"
	| "/onboarding/signup-heard-about"
	| "/onboarding/signup-intention"
	| "/onboarding/signup-instagram"
	| "/onboarding/signup-email"

interface SignupStep {
	id: SignupStepId
	path: SignupStepPath
	collects: ReadonlyArray<keyof RegistrationFormValues>
	requires: ReadonlyArray<keyof RegistrationFormValues>
}

const SIGNUP_STEPS: readonly SignupStep[] = [
	{
		id: "language",
		path: "/onboarding/language",
		collects: ["appLanguage"],
		requires: [],
	},
	{
		id: "name",
		path: "/onboarding/name",
		collects: ["firstName", "consent"],
		requires: [],
	},
	{
		id: "gender",
		path: "/onboarding/signup-gender",
		collects: ["gender"],
		requires: ["firstName"],
	},
	{
		id: "city",
		path: "/onboarding/signup-city",
		collects: ["city"],
		requires: ["firstName", "gender"],
	},
	{
		id: "heard-about",
		path: "/onboarding/signup-heard-about",
		collects: [],
		requires: ["firstName", "gender", "city"],
	},
	{
		id: "intention",
		path: "/onboarding/signup-intention",
		collects: [],
		requires: ["firstName", "gender", "city"],
	},
	{
		id: "instagram",
		path: "/onboarding/signup-instagram",
		collects: [],
		requires: ["firstName", "gender", "city"],
	},
	{
		id: "email",
		path: "/onboarding/signup-email",
		collects: ["email"],
		requires: ["appLanguage", "firstName", "gender", "city"],
	},
]

function indexOfSignupStep(id: SignupStepId): number {
	return SIGNUP_STEPS.findIndex((step) => step.id === id)
}

export function nextSignupStepPath(
	id: SignupStepId,
): SignupStepPath | undefined {
	return SIGNUP_STEPS[indexOfSignupStep(id) + 1]?.path
}

export function previousSignupStepPath(
	id: SignupStepId,
): SignupStepPath | undefined {
	return SIGNUP_STEPS[indexOfSignupStep(id) - 1]?.path
}

function isFilled(value: unknown): boolean {
	return value != null && value !== ""
}

export function enforceSignupStep(id: SignupStepId): void {
	const step = SIGNUP_STEPS[indexOfSignupStep(id)]
	if (!step) return

	const values = getOnboardingFormValues()
	for (const field of step.requires) {
		if (isFilled(values?.[field])) continue
		const collector = SIGNUP_STEPS.find((candidate) =>
			candidate.collects.includes(field),
		)
		if (collector) throw redirect({ to: collector.path })
	}
}
